import { Link } from "react-router";

import { Tag } from "../lib/Tag.tsx";
import { Panel } from "./ProfilePanels.tsx";
import { PROFILE_COPY } from "./profile.constants.ts";
import type { EntityRow, HoldingRow } from "./profile.model.ts";

/**
 * Entities you belong to, and collections you hold.
 *
 * Two lists rather than one, because they are two kinds of thing: an
 * organisation is a principal somebody else granted you a membership of, and a
 * collection is the unit of access in this registry — "a collection is the unit
 * of access, a work is the unit of record", the first line of the model.
 *
 * An entity row links to that organisation's own workspace, and `href` is null
 * for a membership the server derived no workspace from. Rendering it as text in
 * that case rather than as a dead link: the membership is real and worth showing,
 * and a link that bounced would say the opposite.
 */
export function ProfileEntities({
  entities,
  holdings,
}: {
  entities: EntityRow[];
  holdings: HoldingRow[];
}) {
  return (
    <>
      <Panel title={PROFILE_COPY.entitiesHeading} note={PROFILE_COPY.entitiesNote}>
        {entities.length === 0 ? (
          <p className="profile__empty">{PROFILE_COPY.entitiesEmpty}</p>
        ) : (
          <ul className="profile__rows">
            {entities.map((entity) => (
              <li className="profile__row" key={entity.ondoId}>
                <span className="profile__initials profile__initials--small" aria-hidden="true">
                  {entity.initials}
                </span>
                <div className="profile__row-body">
                  {entity.href ? (
                    <Link className="profile__row-title profile__row-link" to={entity.href}>
                      {entity.name}
                    </Link>
                  ) : (
                    <span className="profile__row-title">{entity.name}</span>
                  )}
                  <span className="profile__row-meta">
                    {entity.meta} · {entity.ondoId}
                  </span>
                </div>
                <Tag tone={entity.verified ? "verified" : "unverified"}>
                  {entity.verified ? PROFILE_COPY.verified : PROFILE_COPY.unverified}
                </Tag>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title={PROFILE_COPY.holdingsHeading}>
        {holdings.length === 0 ? (
          <p className="profile__empty">{PROFILE_COPY.holdingsEmpty}</p>
        ) : (
          <ul className="profile__rows">
            {holdings.map((holding) => (
              <li className="profile__row" key={holding.id}>
                <div className="profile__row-body">
                  <span className="profile__row-title">{holding.name}</span>
                  <span className="profile__row-meta">{holding.holdings}</span>
                </div>
                <Tag tone="neutral">{holding.level}</Tag>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  );
}
